import { Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { lastValueFrom } from 'rxjs';
import { defaultNavigation } from 'app/mock-api/common/navigation/data';
import { TeraComponentService } from 'app/shared/services/profiles-permissions/teraComponent.service';
import { TeraComponent } from 'app/shared/models/profiles-permissions/teraComponent.model';
import { ParameterService } from 'app/shared/services/parameters/parameter.service';

@Injectable({
  providedIn: 'root'
})

/// Synchronisation des composants de type Page avec la navigation de l'application = data.ts
/// This service synchronizes the application's navigation structure with the backend resources.
/// It ensures that each navigation item has a corresponding Resource of type Page.

export class NavigationSyncService {
  private byCode = new Map<string, TeraComponent>();
  private navigationCodes = new Set<string>();
  private componentTypePageId: string | null = null;

  constructor(
    private readonly _teraComponentService: TeraComponentService,
    private readonly _parameterService: ParameterService
  ) {}

  async init(): Promise<void> {
    try {      
      const componentTypePage = await lastValueFrom(this._parameterService.getByCodes('PAGE', 'COMPONENT_TYPE'));
      this.componentTypePageId = componentTypePage?.id ?? null;

      if (!this.componentTypePageId) {
        console.warn('NavigationSyncService: PAGE component type not found; sync skipped');
        return;
      }

      // defaultNavigation contains TERA pages only. Never include SICA components
      // in this synchronization.
      const existing = await lastValueFrom(this._teraComponentService.getAll(false));
      this.byCode.clear();
      this.navigationCodes.clear();
      this.collectNavigationCodes(defaultNavigation);
      for (const r of existing) {
        if (r.appComponentId) this.byCode.set(r.appComponentId.trim().toLowerCase(), r);
      }

      for (let i = 0; i < defaultNavigation.length; i++) {
        await this.processItem(defaultNavigation[i], null, i + 1);
      }

      await this.deleteOrphanPages(existing);
    } catch (e) {
      console.warn('NavigationSyncService: sync failed', e);
    }
  }

  private collectNavigationCodes(items: FuseNavigationItem[]): void {
    for (const item of items) {
      if (item.id) this.navigationCodes.add(item.id.trim().toLowerCase());
      if (item.children?.length) this.collectNavigationCodes(item.children);
    }
  }

  private async processItem(item: any, parentId: string | null, order: number): Promise<void> {
    if (!item.id) return;

    const key = item.id.trim().toLowerCase();
    let teraComponent = this.byCode.get(key);
    if (teraComponent) {
      // Resolve existing parentId (backend may return it via nested parent object)
      const existingParentId = teraComponent.teraComponentParentId || (teraComponent as any)?.teraComponentParent?.id || null;

      // Update if name, description, type or parentId differ
      const needsUpdate =
        (teraComponent.name || '') !== (item.title || '') ||
        (teraComponent.title || '') !== (item.title || '') ||       
        (teraComponent.description || '') !== (item.description || '') ||
        teraComponent.componentTypeId !== this.componentTypePageId ||
        (existingParentId || null) !== (parentId || null) ||
        teraComponent.order !== order;

      if (needsUpdate) {
        teraComponent.name = item.title;
        teraComponent.title = item.title;         
        teraComponent.description = item.description || null;
        teraComponent.componentTypeId = this.componentTypePageId;
        teraComponent.teraComponentParentId = parentId;
        teraComponent.order = order;
        try {
          await lastValueFrom(this._teraComponentService.update(teraComponent));
        } catch { /* ignore */ }
      }
    } else {
      // Create with parentId passed from the parent call
      const newRes = new TeraComponent({
        appComponentId: item.id,
        name: item.title,
        title: item.title,       
        description: item.description || null,
        componentTypeId: this.componentTypePageId,
        teraComponentParentId: parentId,
        order
      });
      // Remove fields that interfere with backend binding
      delete (newRes as any).parent;
      delete (newRes as any).id;

      try {
        teraComponent = await lastValueFrom(this._teraComponentService.add(newRes));
        this.byCode.set(key, teraComponent);
      } catch { /* ignore */ }
    }

    // Process children — pass this resource's GUID directly
    if (item.children?.length && teraComponent?.id) {
      for (let i = 0; i < item.children.length; i++) {
        await this.processItem(item.children[i], teraComponent.id, i + 1);
      }
    }
  }

  private async deleteOrphanPages(existing: TeraComponent[]): Promise<void> {
    const existingById = new Map<string, TeraComponent>();
    for (const component of existing) {
      if (component?.id) {
        existingById.set(component.id, component);
      }
    }

    const depthMemo = new Map<string, number>();
    const getDepth = (component: TeraComponent): number => {
      if (!component?.id) return 0;
      if (depthMemo.has(component.id)) return depthMemo.get(component.id)!;

      const parent = component.teraComponentParentId
        ? existingById.get(component.teraComponentParentId)
        : null;
      const depth = parent ? getDepth(parent) + 1 : 0;
      depthMemo.set(component.id, depth);
      return depth;
    };

    const orphanPages = existing
      .filter(component => {
        if (component?.isSica !== false || component?.componentTypeId !== this.componentTypePageId) {
          return false;
        }

        const code = (component.appComponentId || '').trim().toLowerCase();
        return !this.navigationCodes.has(code);
      })
      .sort((first, second) => getDepth(second) - getDepth(first));

    for (const page of orphanPages) {
      try {
        const result = await lastValueFrom(this._teraComponentService.delete(page.id));
        if (result?.success === false) {
          throw new Error('Component deletion was rejected by the API');
        }
        this.byCode.delete((page.appComponentId || '').trim().toLowerCase());
      } catch (error) {
        console.warn(`NavigationSyncService: could not delete orphan page ${page.appComponentId}`, error);
      }
    }
  }
}
